import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { checkFollowStatusApi } from "../../../api/resources.api";

type FollowableProject = {
  id: number;
  is_following?: boolean;
};

export const useProjectsFollowCheck = (projects: FollowableProject[]) => {
  const projectIds = useMemo(() => projects.map((project) => project.id), [projects]);

  const followQueries = useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: ["followCheck", "projects", projectId],
      queryFn: async () => {
        const response = await checkFollowStatusApi("projects", projectId);
        if (typeof response?.is_following !== "boolean") {
          throw new Error("Invalid follow check response");
        }
        return response.is_following;
      },
      staleTime: 0,
      refetchOnMount: "always",
      retry: 1,
      refetchOnWindowFocus: false,
    })),
  });

  const followStatusMap = useMemo(() => {
    const initialMap = projects.reduce<Record<number, boolean>>((acc, project) => {
      acc[project.id] = !!project.is_following;
      return acc;
    }, {});

    return followQueries.reduce<Record<number, boolean>>((acc, query, index) => {
      const projectId = projectIds[index];
      if (!projectId) return acc;

      if (typeof query.data === "boolean") {
        acc[projectId] = query.data;
      }

      return acc;
    }, initialMap);
  }, [projects, followQueries, projectIds]);

  const loadingMap = useMemo(
    () =>
      followQueries.reduce<Record<number, boolean>>((acc, query, index) => {
        const projectId = projectIds[index];
        if (!projectId) return acc;
        acc[projectId] = query.isLoading || query.isFetching;
        return acc;
      }, {}),
    [followQueries, projectIds]
  );

  return { followStatusMap, loadingMap };
};
